/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Typography, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible, setModalQuotationVisible } from "@store/slices/appSlice";
import { setNewConditionLoan } from "@store/slices/registerSlice";
import { ApiError, LoanCondiciones } from "@interfaces/response";
import { TaxSystemConfigResponse } from "@interfaces/index";
import {
  useLazyGetConditionsByIdQuery,
  useLazyGetTaxSystemConfigQuery,
  usePostConditionMutation,
} from "@api/applications";
import CenteredLoader from "@components/CenteredLoader";
import ModalCloseButton from "@components/ModalCloseButton";
import { StyledSlider } from "@components/CustomSlider";
import { amountToText, formatCurrency } from "@helpers/funciones";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import * as Sentry from "@sentry/react";
import { calculateCreditQuote, calculateLeasingQuote, LEASING_DEFAULTS } from "./helpers";

// CVMPF = crédito simple, CVAPF = arrendamiento
const VIVEMOTO_CODES = {
  CREDITO: "CVMPF",
  ARRENDAMIENTO: "CVAPF",
};

interface SliderParams {
  min: number;
  max: number;
  step: number;
}

const buildMarks = (min: number, max: number, format: (value: number) => string) => {
  if (!(min < max)) return [];
  return [
    { value: min, label: format(min) },
    { value: max, label: format(max) },
  ];
};

const QuotationModalVivemoto = () => {
  // hooks
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();

  // redux hooks
  const modalState = useSelector((state: RootState) => state.app.modal_quotation);
  const loanApplication = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [triggerPostCondition] = usePostConditionMutation();

  // state
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);
  const [config, setConfig] = useState<TaxSystemConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [amount, setAmount] = useState<number>(0);
  const [term, setTerm] = useState<number>(0);
  const [advance, setAdvance] = useState<number>(0);

  const isLeasing = loanApplication.type === VIVEMOTO_CODES.ARRENDAMIENTO;
  const residual = LEASING_DEFAULTS.residualPercent;

  // rangos: se usan los aprobados en la condición y, si no vienen, los del tax system
  const amountParams: SliderParams = useMemo(() => {
    const min = config?.minAmount ?? 0;
    const configMax = config?.maxAmount ?? 0;
    const approved = condition?.approved_amount ?? 0;
    const max = approved > 0 && approved < configMax ? approved : configMax;
    return { min, max, step: config?.stepAmount || 1 };
  }, [config, condition]);

  const termParams: SliderParams = useMemo(
    () => ({
      min: condition?.approved_min_term ?? config?.minTerm ?? 0,
      max: condition?.approved_max_term ?? config?.maxTerm ?? 0,
      step: config?.stepTerm || 1,
    }),
    [config, condition],
  );

  const advanceParams: SliderParams = useMemo(
    () => ({
      min: condition?.approved_min_advance_percentage ?? config?.minAdvance ?? 0,
      max: condition?.approved_max_advance_percentage ?? config?.maxAdvance ?? 0,
      step: config?.stepAdvance || 1,
    }),
    [config, condition],
  );

  const monthlyPayment = useMemo(() => {
    if (!amount || !term) return 0;

    const rawRate = condition?.interest_rate || interfaceConfig?.interest_rate || LEASING_DEFAULTS.annualRate;
    // la tasa puede venir como fracción (0.4) o como porcentaje (40)
    const annualRate = rawRate <= 1 ? rawRate * 100 : rawRate;
    const commissionPercent = interfaceConfig?.comision ?? LEASING_DEFAULTS.commissionPercent;

    if (isLeasing) {
      const { monthlyPaymentWithIva } = calculateLeasingQuote({
        ...LEASING_DEFAULTS,
        annualRate,
        commissionPercent,
        equipmentValue: amount,
        termMonths: term,
        advancePercent: advance,
        residualPercent: residual,
      });
      return Number(monthlyPaymentWithIva.toFixed(2));
    }

    const { monthlyPayment } = calculateCreditQuote({
      unitValue: amount,
      termMonths: term,
      annualRate,
      commissionPercent,
      downPaymentPercent: advance,
    });
    return Number(monthlyPayment.toFixed(2));
  }, [amount, term, advance, isLeasing, condition?.interest_rate, interfaceConfig]);

  const hasChange =
    !!condition &&
    (condition.selected_amount !== amount ||
      condition.selected_term !== term ||
      (condition.selected_advance_percentage ?? 0) !== advance);

  const handleOnClickClose = () => {
    dispatch(setModalQuotationVisible(false));
  };

  const handleOnClickUpdateConditionLoan = async () => {
    try {
      setIsSaving(true);
      await triggerPostCondition({
        loan_application: loanApplication.id,
        loan_amount: amount,
        requested_term: term,
        selected_amount: amount,
        selected_term: term,
        requested_amount: amount,
        selected_advance_percentage: advance,
        requested_advance_percentage: advance,
        ...(isLeasing && {
          selected_residual_percentage: residual,
          requested_residual_percentage: residual,
        }),
        user_name: `TOFU - ${accountUser.email}`,
        user_id: String(accountUser.id),
      }).unwrap();

      dispatch(
        setNewConditionLoan({
          approved_amount: loanApplication.approved_amount,
          approved_term: loanApplication.approved_term,
          interest_rate: loanApplication.interest_rate,
          loanAmount: amount,
          loanTerm: term,
          amountMonthly: monthlyPayment,
        }),
      );
      dispatch(setModalQuotationVisible(false));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "QuotationModalVivemoto",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (modalState?.visible === false) return;

        setIsLoading(true);

        const [conditions, taxSystemConfig] = await Promise.all([
          getConditions(loanApplication.id).unwrap(),
          getTaxSystemConfig(loanApplication.type).unwrap(),
        ]);

        const activeCondition = conditions.find((item) => item.active);
        if (activeCondition) {
          setCondition(activeCondition);
          setConfig(taxSystemConfig);
          setAmount(activeCondition.selected_amount);
          setTerm(activeCondition.selected_term);
          setAdvance(activeCondition.selected_advance_percentage ?? taxSystemConfig.defaultAdvance ?? 0);
        }
      } catch (error) {
        console.log("Ha ocurrido un error: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [modalState?.visible]);

  if (isLoading) {
    return (
      <Dialog open={modalState?.visible} maxWidth={"sm"}>
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"} width={"280px"} height={"400px"}>
          <CenteredLoader />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={modalState?.visible} maxWidth={"sm"}>
      <DialogTitle sx={{ position: "relative", pr: 6, pt: 2 }}>
        <Typography variant="body1" color={"#002652"} fontWeight={600} textAlign={"center"}>
          Modifica tu cotización
        </Typography>
        <ModalCloseButton isSaving={isSaving} handleOnClickClose={handleOnClickClose} />
      </DialogTitle>
      <DialogContent>
        <Box mx={2} textAlign={"center"}>
          <Typography variant="body2" color={theme?.palette?.primary?.main} fontWeight={600} mb={2}>
            {isLeasing ? "Arrendamiento" : "Crédito simple"}
          </Typography>

          <Typography fontWeight={500} fontSize={"16px"} color={theme?.palette?.primary?.dark}>
            Selecciona el precio de la moto
          </Typography>
          <Typography fontWeight={700} fontSize={"13px"} mt={1} color={theme?.palette?.primary?.main}>
            {formatCurrency(amount)}
          </Typography>
          <StyledSlider
            aria-label="amount"
            min={amountParams.min}
            max={amountParams.max}
            step={amountParams.step}
            marks={buildMarks(amountParams.min, amountParams.max, amountToText)}
            value={amount}
            onChange={(_event, newValue) => setAmount(newValue as number)}
            disabled={isSaving}
          />

          <Typography fontWeight={500} fontSize={"16px"} mt={2} mb={2} color={theme?.palette?.primary?.dark}>
            Selecciona el plazo en meses
          </Typography>
          <StyledSlider
            aria-label="term"
            min={termParams.min}
            max={termParams.max}
            step={termParams.step}
            marks={buildMarks(termParams.min, termParams.max, (value) => `${value}`)}
            value={term}
            onChange={(_event, newValue) => setTerm(newValue as number)}
            valueLabelDisplay="on"
            disabled={isSaving}
          />

          <Typography fontWeight={500} fontSize={"16px"} mt={2} mb={2} color={theme?.palette?.primary?.dark}>
            Anticipo
          </Typography>
          <StyledSlider
            aria-label="advance"
            min={advanceParams.min}
            max={advanceParams.max}
            step={advanceParams.step}
            marks={buildMarks(advanceParams.min, advanceParams.max, (value) => `${value}%`)}
            value={advance}
            onChange={(_event, newValue) => setAdvance(newValue as number)}
            valueLabelDisplay="on"
            disabled={isSaving}
          />

          <Typography fontWeight={700} fontSize={"16px"} mt={3} color={theme?.palette?.primary?.main}>
            Pago mensual estimado*
          </Typography>
          <Typography
            variant="h4"
            fontWeight={theme?.palette?.fontWeights?.semibold}
            color={theme?.palette?.primary?.dark}
          >
            {formatCurrency(monthlyPayment)}{" "}
            <Typography
              variant="h5"
              component={"span"}
              fontWeight={theme?.palette?.fontWeights?.semibold}
              color={theme?.palette?.primary?.dark}
            >
              MXN
            </Typography>
          </Typography>

          <Grid container columnSpacing={1} my={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="blue_outlined"
                sx={{ padding: "0", height: "100%" }}
                onClick={handleOnClickClose}
              >
                Regresar
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="default"
                size="medium"
                onClick={handleOnClickUpdateConditionLoan}
                disabled={isSaving || !hasChange || !monthlyPayment}
              >
                Continuar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QuotationModalVivemoto;
